// Primary Sales Return approval inbox — ASM approves first, then ZSM (final).
// One shared page: which action endpoint fires depends on the caller's own
// role, same as the two-stage TA/DA approval screen.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, X, PackageMinus, Download } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { authenticatedDownload } from "@/components/bulk/useBulkJob";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import PageHeader from "@/components/PageHeader";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const STATUS_STYLE = {
  submitted:     "bg-amber-50 text-amber-700 border-amber-200",
  asm_approved:  "bg-sky-50 text-sky-700 border-sky-200",
  asm_rejected:  "bg-rose-50 text-rose-700 border-rose-200",
  zsm_approved:  "bg-emerald-50 text-emerald-700 border-emerald-200",
  zsm_rejected:  "bg-rose-50 text-rose-700 border-rose-200",
};
function statusLabel(s) { return (s || "").replace(/_/g, " "); }

export default function PrimarySaleReturnApproval() {
  const { user } = useAuth();
  // ASM acts on "submitted" rows, ZSM on "asm_approved" rows. SUPER_ADMIN/
  // MANAGEMENT can see + act on either stage (same override pattern as the
  // backend's ASM_ROLES/ZSM_ROLES).
  const isZsm = user?.role_code === "ZSM";
  const stage = isZsm ? "zsm" : "asm";
  const pendingStatus = isZsm ? "asm_approved" : "submitted";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(pendingStatus);
  const [decisionRow, setDecisionRow] = useState(null); // {row, action}
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [pdfBusyId, setPdfBusyId] = useState(null);

  const downloadPdf = async (row) => {
    setPdfBusyId(row.id);
    try {
      await authenticatedDownload(`/primary-sale-returns/${row.id}/pdf`, `${row.return_no}.pdf`);
    } catch (e) { toast.error(`PDF download failed: ${e?.message || e}`); }
    finally { setPdfBusyId(null); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page_size: 100 };
      if (statusFilter) params.status = statusFilter;
      const r = await api.get("/primary-sale-returns", { params });
      setRows(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const openDecision = (row, action) => { setDecisionRow({ row, action }); setNotes(""); };

  const confirmDecision = async () => {
    if (!decisionRow) return;
    setBusy(true);
    try {
      await api.post(`/primary-sale-returns/${decisionRow.row.id}/${stage}-decision`, {
        action: decisionRow.action, notes: notes.trim() || null,
      });
      toast.success(decisionRow.action === "approve" ? "Approved" : "Rejected");
      setDecisionRow(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 space-y-4" data-testid="psr-approval-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Money" }, { label: "Primary Sale Return Approval" }]}
        title="Primary Sale Return Approval"
        subtitle={isZsm
          ? "Final stage — your approval closes the return."
          : "First stage — approving sends it to ZSM for final sign-off."}
      />

      <div className="flex flex-wrap gap-1.5">
        {["", "submitted", "asm_approved", "asm_rejected", "zsm_approved", "zsm_rejected"].map((s) => (
          <button key={s || "all"} type="button" onClick={() => setStatusFilter(s)}
                  data-testid={`psr-approval-filter-${s || "all"}`}
                  className={
                    "rounded-full border px-2.5 py-1 text-xs font-medium " +
                    (statusFilter === s ? "bg-[#6D2C91] border-[#6D2C91] text-white" : "bg-white border-neutral-200 text-neutral-700")
                  }>
            {s ? statusLabel(s) : "All"}
          </button>
        ))}
      </div>

      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin mx-auto text-neutral-400" />
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-sm text-neutral-500" data-testid="psr-approval-empty">
          No returns in this filter.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <Card key={r.id} className="border-neutral-200 rounded-2xl" data-testid={`psr-approval-row-${r.id}`}>
              <CardContent className="p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-neutral-900 flex items-center gap-1.5">
                      <PackageMinus className="h-4 w-4 text-[#6D2C91]" /> {r.return_no}
                    </div>
                    <div className="text-[11px] text-neutral-500">
                      {r.distributor_name} ({r.distributor_code}) · {r.total_kg} kg · {new Date(r.created_at).toLocaleString()}
                    </div>
                  </div>
                  <Badge className={"border text-[10px] capitalize " + (STATUS_STYLE[r.status] || "")}>
                    {statusLabel(r.status)}
                  </Badge>
                </div>

                <div className="border-t border-dashed border-neutral-200 pt-2 space-y-1">
                  {r.lines.map((ln) => (
                    <div key={ln.id} className="text-xs flex items-center justify-between text-neutral-700">
                      <span>{ln.sku_code} — {ln.sku_name}</span>
                      <span className="text-neutral-500">
                        {ln.qty_kg} kg · {ln.packing_month} · {ln.reason_label || ln.reason_code} · {ln.type === "scheme" ? `Scheme: ${ln.scheme_name}` : "Plain"}
                      </span>
                    </div>
                  ))}
                </div>
                {r.remarks && <div className="text-[11px] text-neutral-500 italic">{r.remarks}</div>}
                {r.by_group?.length > 1 && (
                  <div className="text-[11px] text-neutral-500">
                    {r.by_group.map((g) => `${g.label}: ${g.qty_kg}kg`).join(" · ")}
                  </div>
                )}

                <Button variant="outline" size="sm" className="w-full"
                        onClick={() => downloadPdf(r)} disabled={pdfBusyId === r.id}
                        data-testid={`psr-download-pdf-${r.id}`}>
                  {pdfBusyId === r.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                  Download PDF
                </Button>

                {r.status === pendingStatus && (
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
                            onClick={() => openDecision(r, "approve")} data-testid={`psr-approve-${r.id}`}>
                      <Check className="h-4 w-4 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 text-rose-700 border-rose-200"
                            onClick={() => openDecision(r, "reject")} data-testid={`psr-reject-${r.id}`}>
                      <X className="h-4 w-4 mr-1" /> Reject
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!decisionRow} onOpenChange={(v) => !v && setDecisionRow(null)}>
        <DialogContent className="max-w-sm" data-testid="psr-decision-dialog">
          <DialogHeader>
            <DialogTitle>
              {decisionRow?.action === "approve" ? "Approve" : "Reject"} {decisionRow?.row?.return_no}?
            </DialogTitle>
          </DialogHeader>
          <Textarea placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)}
                     data-testid="psr-decision-notes" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionRow(null)} data-testid="psr-decision-cancel">
              Cancel
            </Button>
            <Button onClick={confirmDecision} disabled={busy}
                    className={decisionRow?.action === "approve" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-rose-600 hover:bg-rose-700 text-white"}
                    data-testid="psr-decision-confirm">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirm {decisionRow?.action === "approve" ? "Approval" : "Rejection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
